import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useMembership } from "../lib/membership";

export default function Header() {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { isPreview, isAuthMember, clearPreview } = useMembership();

    const submitSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setOpen(false);
        }
    };

    return (
        <header className="sticky top-0 z-50 bg-[#0B1220]/85 backdrop-blur-xl border-b border-[#243044]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <Link to="/" data-testid="header-logo" aria-label="The Context Project" className="flex items-center gap-3 group flex-shrink-0">
                        <div className="h-9 px-2 flex items-center justify-center rounded-md bg-white">
                            <img
                                src={require("../assets/logo-tcp-mark.png")}
                                alt="The Context Project"
                                className="h-6 w-auto select-none"
                                draggable={false}
                            />
                        </div>
                    </Link>

                    <nav className="hidden md:flex items-center gap-5">
                        <Link to="/" data-testid="nav-library" className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                            Library
                        </Link>
                        <Link to="/timeline" data-testid="nav-timeline" className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                            Timeline
                        </Link>
                        <Link to="/entities" data-testid="nav-entities" className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                            Entities
                        </Link>
                        <Link to="/sources" data-testid="nav-sources" className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                            Sources
                        </Link>
                        <Link to="/vaccines" data-testid="nav-vaccines" className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                            Vaccines
                        </Link>
                        <Link to="/saved" data-testid="nav-saved" className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                            Saved
                        </Link>
                        <Link to="/methodology" data-testid="nav-methodology" className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                            Methodology
                        </Link>
                        <Link to="/about" data-testid="nav-about" className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                            About
                        </Link>
                        <a
                            href="https://thecontextproject.com"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-main-site"
                            className="text-sm font-medium text-[#94A3B8] hover:text-[#56CCF2] transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
                        >
                            Main site
                            <span className="text-[10px] opacity-60">↗</span>
                        </a>
                    </nav>

                    <div className="flex items-center gap-3">
                        {(isPreview || isAuthMember) && (
                            <span
                                data-testid="header-member-pill"
                                className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#56CCF2]/10 border border-[#56CCF2]/30 text-[10px] uppercase tracking-[0.2em] text-[#56CCF2] font-medium"
                                title={isAuthMember ? "Member access" : "Preview link active"}
                            >
                                <Eye className="w-3 h-3" />
                                {isAuthMember ? "Member" : "Preview"}
                                {isPreview && !isAuthMember && (
                                    <button
                                        type="button"
                                        onClick={() => { clearPreview(); window.location.reload(); }}
                                        data-testid="header-preview-clear"
                                        className="ml-1 text-[#56CCF2]/70 hover:text-white"
                                        aria-label="Clear preview"
                                    >
                                        <X className="w-2.5 h-2.5" />
                                    </button>
                                )}
                            </span>
                        )}
                        <form onSubmit={submitSearch} className="hidden md:flex items-center bg-[#111827] border border-[#243044] rounded-lg px-3 py-1.5 focus-within:border-[#2F80ED] transition-colors">
                            <Search className="w-4 h-4 text-[#64748B]" />
                            <input
                                data-testid="header-search-input"
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search topics…"
                                className="bg-transparent border-0 outline-none text-sm text-white placeholder-[#64748B] ml-2 w-44"
                            />
                        </form>

                        {user ? (
                            <div className="hidden md:flex items-center gap-3">
                                <Link
                                    to="/dashboard"
                                    data-testid="nav-dashboard"
                                    className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors"
                                >
                                    Dashboard
                                </Link>
                                {user.role === "admin" && (
                                    <Link
                                        to="/admin"
                                        data-testid="nav-admin"
                                        className="text-sm font-medium text-[#56CCF2] hover:text-white transition-colors"
                                    >
                                        Admin
                                    </Link>
                                )}
                                <button
                                    data-testid="nav-logout"
                                    onClick={logout}
                                    className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors"
                                >
                                    Sign out
                                </button>
                            </div>
                        ) : (
                            <div className="hidden md:flex items-center gap-2">
                                <Link
                                    to="/account/login"
                                    data-testid="nav-login"
                                    className="text-sm font-medium px-4 py-1.5 rounded-lg border border-[#243044] text-[#94A3B8] hover:text-white hover:border-[#2F80ED] transition-colors"
                                >
                                    Sign in
                                </Link>
                                <Link
                                    to={process.env.REACT_APP_MEMBERSHIP_URL || "/membership"}
                                    data-testid="nav-become-member-link"
                                    className="text-sm font-medium px-4 py-1.5 rounded-lg bg-[#2F80ED] hover:bg-[#1E6FE0] text-white transition-colors"
                                >
                                    Become a member
                                </Link>
                            </div>
                        )}

                        <button
                            data-testid="mobile-menu-toggle"
                            className="md:hidden p-2 text-[#94A3B8]"
                            onClick={() => setOpen((v) => !v)}
                        >
                            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {open && (
                    <div className="md:hidden border-t border-[#243044] py-4 space-y-3">
                        <form onSubmit={submitSearch} className="flex items-center bg-[#111827] border border-[#243044] rounded-lg px-3 py-2">
                            <Search className="w-4 h-4 text-[#64748B]" />
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search topics…"
                                className="bg-transparent border-0 outline-none text-sm text-white placeholder-[#64748B] ml-2 flex-1"
                                data-testid="mobile-search-input"
                            />
                        </form>
                        <Link to="/" className="block text-sm text-[#94A3B8] py-2">Library</Link>
                        <Link to="/methodology" className="block text-sm text-[#94A3B8] py-2">Methodology</Link>
                        <Link to="/timeline" className="block text-sm text-[#94A3B8] py-2">Timeline</Link>
                        <Link to="/entities" className="block text-sm text-[#94A3B8] py-2">Entities</Link>
                        <Link to="/sources" className="block text-sm text-[#94A3B8] py-2">Sources</Link>
                        <Link to="/vaccines" className="block text-sm text-[#94A3B8] py-2">Vaccines</Link>
                        <Link to="/saved" className="block text-sm text-[#94A3B8] py-2">Saved</Link>
                        <Link to="/about" className="block text-sm text-[#94A3B8] py-2">About</Link>
                        <a href="https://thecontextproject.com" target="_blank" rel="noreferrer" className="block text-sm text-[#56CCF2] py-2">Main site ↗</a>
                        {user ? (
                            <>
                                <Link to="/dashboard" className="block text-sm text-white py-2">Dashboard</Link>
                                {user.role === "admin" && (
                                    <Link to="/admin" className="block text-sm text-[#56CCF2] py-2">Admin</Link>
                                )}
                                <button onClick={logout} className="block text-sm text-[#94A3B8] py-2">Sign out</button>
                            </>
                        ) : (
                            <>
                                <Link to="/account/login" className="block text-sm text-[#94A3B8] py-2">Sign in</Link>
                                <Link
                                    to={process.env.REACT_APP_MEMBERSHIP_URL || "/membership"}
                                    onClick={() => setOpen(false)}
                                    className="block text-sm text-[#56CCF2] py-2"
                                >
                                    Become a member →
                                </Link>
                            </>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
}
